import { Link } from '@tanstack/react-router'
import { BadgeCheck } from 'lucide-react'
import { SonCta } from '../components/cta'
import { Reveal, Stagger, StaggerItem } from '../components/hareket'
import { Mockup } from '../components/mockup'
import { ModulKarti } from '../components/modul-karti'
import { SssAkordeon } from '../components/sss-akordeon'
import { Baslik, ButonLink, DEMO_YOLU, Eyebrow, Rozet, SayfaUstu, Section } from '../components/ui'
import { DURUM_ETIKETI, MODULLER, modulBul } from '../icerik/moduller'
import { tl } from '../icerik/planlar'
import { useHead } from '../lib/head'

export function Ozellikler() {
  useHead(
    {
      title: 'Özellikler',
      description:
        'Üye yönetimi, aidat ve tahsilat, web sitesi, e-posta ve SMS, üye portalı, kurullar ve genel kurul, gelir-gider, bağış, kongre, akademi, mağaza, güvenlik.',
    },
    '/ozellikler',
  )
  const gruplar: Array<{ ad: string; aciklama: string; grup: 'cekirdek' | 'paket' | 'ust' }> = [
    {
      ad: 'Çekirdek',
      aciklama: 'Her planda var. Derneğin günlük işi bunlarla döner.',
      grup: 'cekirdek',
    },
    {
      ad: 'Paketler',
      aciklama: 'Plana aylık eklenir. İhtiyacınız olduğunda açar, bittiğinde kapatırsınız.',
      grup: 'paket',
    },
    {
      ad: 'Platform',
      aciklama: 'Bütün modüllerin altında duran güvenlik ve uyum katmanı.',
      grup: 'ust',
    },
  ]
  return (
    <>
      <SayfaUstu>
        <Section className="pt-0 sm:pt-0">
          <Reveal>
            <Baslik
              seviye="h1"
              eyebrow="Özellikler"
              baslik="Derneğin bütün işi için modüller"
              metin="Her modül aynı üye kaydını, aynı sipariş çekirdeğini ve aynı denetim kaydını kullanır. Bir yerde girdiğiniz bilgi başka yerde yeniden istenmez."
            />
          </Reveal>
          {gruplar.map((g) => (
            <div key={g.grup} className="mt-14">
              <Reveal>
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="heading text-2xl text-ink">{g.ad}</h2>
                  <p className="text-sm text-ink-3">{g.aciklama}</p>
                </div>
              </Reveal>
              <Stagger className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {MODULLER.filter((m) => m.grup === g.grup).map((m) => (
                  <StaggerItem key={m.slug} className="h-full">
                    <ModulKarti modul={m} />
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          ))}
        </Section>
      </SayfaUstu>
      <SonCta />
    </>
  )
}

export function OzellikSayfasi({ slug }: { slug: string }) {
  const m = modulBul(slug)
  const yol = `/ozellikler/${slug}`
  useHead({ title: m.ad, description: m.kisa }, yol)
  const rozet = DURUM_ETIKETI[m.durum]
  const Ikon = m.ikon
  return (
    <>
      <SayfaUstu>
        <Section className="pt-0 sm:pt-0">
          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
            <Reveal>
              <div className="flex flex-wrap items-center gap-2">
                <span className="grid size-10 place-items-center rounded-[var(--radius-card)] bg-brand-subtle text-brand">
                  <Ikon className="size-5" />
                </span>
                <Eyebrow>
                  {m.grup === 'paket' ? 'Paket' : m.grup === 'ust' ? 'Platform' : 'Çekirdek modül'}
                </Eyebrow>
                {rozet && (
                  <Rozet tone="warning">
                    {rozet} · {m.faz}
                  </Rozet>
                )}
              </div>
              <h1 className="display mt-5 text-balance text-4xl text-ink sm:text-5xl">{m.ad}</h1>
              <p className="mt-5 text-pretty text-lg leading-relaxed text-ink-2">{m.aciklama}</p>
              <div className="mt-7 flex flex-wrap gap-3">
                <ButonLink to={DEMO_YOLU} ok>
                  Demo isteyin
                </ButonLink>
                {m.paketFiyati && (
                  <span className="inline-flex h-11 items-center rounded-[var(--radius-control)] bg-subtle px-4 text-sm text-ink-2">
                    Plana ek{' '}
                    <span className="data mx-1 font-semibold text-ink">{tl(m.paketFiyati)}</span> /
                    ay
                  </span>
                )}
              </div>
            </Reveal>
            <Reveal delay={0.1} y={24}>
              <Mockup anahtar={m.mockup} />
            </Reveal>
          </div>
        </Section>
      </SayfaUstu>

      <Section className="bg-surface/60">
        <Reveal>
          <Baslik eyebrow="Neler var" baslik={`${m.ad}: özellikler`} />
        </Reveal>
        <Stagger className="mt-10 grid gap-x-8 gap-y-7 sm:grid-cols-2 lg:grid-cols-3">
          {m.ozellikler.map((o) => (
            <StaggerItem key={o.baslik}>
              <div className="flex gap-3">
                <BadgeCheck className="mt-1 size-4 shrink-0 text-brand" aria-hidden />
                <div>
                  <p className="font-semibold text-ink">{o.baslik}</p>
                  <p className="mt-1 text-sm leading-relaxed text-ink-2">{o.aciklama}</p>
                </div>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </Section>

      <Section>
        <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr] lg:gap-16">
          <Reveal>
            <Baslik eyebrow="Kullanım senaryosu" baslik={m.senaryo.baslik} />
          </Reveal>
          <Reveal delay={0.1}>
            <ol className="relative space-y-6 border-l border-line pl-8">
              {m.senaryo.metin.map((p, i) => (
                <li key={i} className="relative">
                  <span className="data absolute -left-[45px] top-0 grid size-8 place-items-center rounded-full bg-surface text-xs font-semibold text-brand ring-1 ring-line">
                    {i + 1}
                  </span>
                  <p className="text-[15px] leading-relaxed text-ink-2">{p}</p>
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </Section>

      <Section className="bg-surface/60">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.6fr]">
          <Reveal>
            <Baslik eyebrow="Sık sorulanlar" baslik={`${m.ad} hakkında`} />
            <div className="mt-8">
              <p className="text-[11px] font-medium uppercase tracking-wider text-ink-3">
                Birlikte çalışır
              </p>
              <ul className="mt-3 space-y-2">
                {m.iliskili.map((s) => {
                  const k = modulBul(s)
                  return (
                    <li key={s}>
                      <Link
                        to={`/ozellikler/${s}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:text-brand"
                      >
                        <k.ikon className="size-4 text-brand" aria-hidden />
                        {k.ad}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SssAkordeon sorular={m.sss} />
          </Reveal>
        </div>
      </Section>
      <SonCta />
    </>
  )
}
